const DEFAULT_CALIBRATION_TEMPERATURE_C = 25.0;
const RAW_SPECTRUM_LENGTH = 18;
const USEFUL_CHANNELS = 14;
const STRAY_CHANNELS = 9;

const sanitizeTemperature = (temperatureC) => {
  if (!Number.isFinite(temperatureC) || temperatureC < -40 || temperatureC > 45) {
    return DEFAULT_CALIBRATION_TEMPERATURE_C;
  }

  return temperatureC;
};

const normalizeRawSpectrum = (rawSpectrum) => {
  if (!Array.isArray(rawSpectrum)) {
    return null;
  }

  return rawSpectrum.slice(0, RAW_SPECTRUM_LENGTH).map((value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  });
};

const hasValidCalibrationCoefficients = (calibration) => (
  calibration
  && Array.isArray(calibration.coeffs)
  && calibration.coeffs.length === 78
  && calibration.coeffs.every((value) => typeof value === 'number' && Number.isFinite(value))
);

export const calibrateSolaceSpectrum = (rawSpectrum, calibration, temperatureC = DEFAULT_CALIBRATION_TEMPERATURE_C) => {
  const normalizedRawSpectrum = normalizeRawSpectrum(rawSpectrum);
  if (!normalizedRawSpectrum) {
    return null;
  }

  if (!calibration?.enabled || !hasValidCalibrationCoefficients(calibration)) {
    return normalizedRawSpectrum.slice(0, USEFUL_CHANNELS);
  }

  const coeffs = calibration.coeffs;
  const output = [...normalizedRawSpectrum];
  temperatureC = sanitizeTemperature(temperatureC);

  const maxRaw = Math.max(0, ...output);
  const nine10 = maxRaw > 0
    ? (output[17] / maxRaw) + (output[16] / maxRaw) + (output[15] / maxRaw)
    : 0;

  const zeroError = Array(RAW_SPECTRUM_LENGTH).fill(0);
  const zeroMask = Math.trunc(coeffs[67]);
  const zeroBits = [
    0x8000, 0x4000, 0x2000, 0x1000, 0x0800, 0x0400, 0x0200,
    0x0100, 0x0080, 0x0040, 0x0020, 0x0010, 0x0008, 0x0004,
  ];

  for (let i = 0; i < USEFUL_CHANNELS; i += 1) {
    if ((zeroMask & zeroBits[i]) !== zeroBits[i]) {
      continue;
    }

    if (i === 2) {
      zeroError[i] = coeffs[69] === 0
        ? coeffs[68] * temperatureC
        : coeffs[69] * Math.exp(coeffs[70] * temperatureC);
    } else if (i === 9) {
      zeroError[i] = coeffs[71] === 0
        ? coeffs[68] * temperatureC
        : coeffs[71] * Math.exp(coeffs[72] * temperatureC);
    } else if (i === 10) {
      zeroError[i] = coeffs[73] === 0
        ? coeffs[68] * temperatureC
        : coeffs[73] * Math.exp(coeffs[74] * temperatureC);
    } else {
      zeroError[i] = coeffs[68] * temperatureC;
    }

    zeroError[i] = Math.min(1, Math.max(0, zeroError[i]));
  }

  const spectral = Array(RAW_SPECTRUM_LENGTH).fill(1);
  for (let i = 0; i < USEFUL_CHANNELS; i += 1) {
    spectral[i] = coeffs[48 + i];
  }

  const stray = Array(RAW_SPECTRUM_LENGTH).fill(1);
  const lower = coeffs[45];
  const upper = coeffs[46];
  const perspex = coeffs[47];
  let upper410 = coeffs[66];

  if (upper410 < 0 || upper410 > 10) {
    upper410 = 2.5;
  }

  if (nine10 > upper) {
    for (let j = 0; j < STRAY_CHANNELS; j += 1) {
      stray[j] = coeffs[36 + j];
    }
  } else if (nine10 >= lower) {
    const x3 = nine10 * nine10 * nine10;
    const x2 = nine10 * nine10;

    for (let j = 0; j < STRAY_CHANNELS; j += 1) {
      let factor =
        x3 * coeffs[j * 4 + 0] +
        x2 * coeffs[j * 4 + 1] +
        nine10 * coeffs[j * 4 + 2] +
        coeffs[j * 4 + 3];

      factor = j === 0
        ? Math.min(upper410, Math.max(0, factor))
        : Math.min(1, Math.max(0, factor));

      stray[j] = factor;
    }
  }

  for (let i = 0; i < USEFUL_CHANNELS; i += 1) {
    let y = output[i];

    if (calibration.zero_error_enabled) {
      y = Math.max(0, y - zeroError[i]);
    }

    if (calibration.spectral_enabled) {
      y *= spectral[i];
    }

    if (calibration.stray_enabled) {
      y *= stray[i] * perspex;
    }

    output[i] = Math.max(0, y);
  }

  return output.slice(0, USEFUL_CHANNELS);
};

export const buildMeasurementSpectra = (rawSpectrum, calibration, temperatureC = DEFAULT_CALIBRATION_TEMPERATURE_C) => {
  const normalizedRawSpectrum = normalizeRawSpectrum(rawSpectrum);
  if (!normalizedRawSpectrum) {
    return null;
  }

  const calibratedSpectrum = calibrateSolaceSpectrum(normalizedRawSpectrum, calibration, temperatureC);

  return {
    raw: normalizedRawSpectrum,
    calibrated: calibratedSpectrum ?? [...normalizedRawSpectrum],
  };
};
